import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutline from '@mui/icons-material/MailOutline';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Tabs,
  TextField,
  Typography,
  Pagination,
  styled,
  useMediaQuery,
  useTheme,
  MenuItem,
  Select,
  SxProps,
  Theme,
  Link,
  Tooltip,
} from '@mui/material';
import { DateTime } from 'luxon';

import { Header } from 'components/Header';
import { Badge } from 'components/jobs/Badge';
import { Footer } from 'components/Footer';
import { CustomTab } from 'components/shared/CustomTab';

import { useRootStore } from 'stores/RootStore';

import { StatusMappings, StatusToColor } from 'dtos/jobs/ReportStatus';
import { JobSummaryOutput } from 'dtos/jobs/JobSummaryOutput';

import { TabType } from 'types/jobs';
import { addressFormatter } from 'utils/formatter';

import BoweryBackgroundWatermark from 'images/boweryBackgroundWatermark.svg';
import PropertyTypeIcon from './PropertyTypeIcon';
import AmplitudeService from 'services/amplitude.service';

const PageHeader = styled('div')(({ theme }) => ({
  background: 'rgba(250, 250, 250, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '53px',
    left: 0,
    width: '100%',
    height: '244px',
    backgroundImage: `url(${BoweryBackgroundWatermark})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    opacity: 0.05,
    [theme.breakpoints.down('md')]: {
      height: '298px',
    },
    [theme.breakpoints.down('sm')]: {
      height: '268px',
    },
  },
  padding: '12px 64px 8px 48px',
  [theme.breakpoints.down('sm')]: {
    padding: '12px 24px',
  },
}));

const PageBody = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  background: 'rgba(250, 250, 250, 1)',
}));

const formatInspectionStatus = (inspectionDate: string | undefined) => {
  if (!inspectionDate) {
    return <span style={{ color: '#ef5350' }}>Not Scheduled</span>;
  }

  const now = DateTime.now();
  const inspection = DateTime.fromISO(inspectionDate);

  if (inspection.startOf('day') >= now.startOf('day')) {
    return <span>{formatDate(inspectionDate)}</span>;
  }

  return <span>Completed</span>;
};

const formatDueDate = (dueDate: string | undefined) => (dueDate ? formatDate(dueDate) : '-');

const formatDate = (date: string | undefined) => {
  if (!date) {
    return null;
  }
  const dateTime = DateTime.fromISO(date);
  return dateTime.toLocaleString(DateTime.DATE_SHORT);
};

const GRID_SETTINGS = {
  PROPERTY_TYPE: { xs: 12, md: 1.75, lg: 0.8 },
  ADDRESS: { xs: 12, md: 5.3, lg: 3 },
  LEAD_APPRAISER: { lg: 2.15 },
  JOB_STATUS: { xs: 12, md: 2.75, lg: 1.4 },
  INSPECTION_STATUS: { lg: 1.1 },
  DUE_DATE: { lg: 1 },
  DILIGENCE_STATUS: { lg: 0.85 },
  ACTIONS: { xs: 12, md: 2.2, lg: 1.7 },
};

const GridHeader = ({ sx = {}, children, ...props }: { sx: SxProps<Theme> | undefined; children: React.ReactNode }) => (
  <Grid item sx={{ textAlign: 'left', ...sx }} {...props}>
    <Typography variant="subtitle1">
      <strong>{children}</strong>
    </Typography>
  </Grid>
);

const JobsViewPage = observer(() => {
  const { jobsViewStore, routeStore } = useRootStore();
  const { jobsFilterStore } = jobsViewStore;
  const { navigateByName } = routeStore;
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const mdScreen = useMediaQuery(theme.breakpoints.down('md'));
  const lgScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const lgAndUpScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const [selectedTab, setSelectedTab] = React.useState(TabType.ActiveReports);
  const [inputText, setInputText] = React.useState('');
  const noDataPadding = smScreen ? '0px 24px' : '0px 48px';

  const isExactLg = lgScreen && !smScreen && !mdScreen;
  const cellsPadding = isExactLg ? '16px 8px' : '16px';
  const paddingLeft = isExactLg ? '8px' : '16px';

  const GridTextStyles = React.useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: cellsPadding,
      paddingTop: smScreen ? '0px' : '16px',
    }),
    [smScreen, cellsPadding]
  );

  React.useEffect(() => {
    jobsFilterStore.search(inputText);
  }, [inputText, jobsFilterStore]);

  const handleClear = () => {
    setInputText('');
  };

  const handleTabChange = (newValue: TabType) => {
    jobsFilterStore.setPage(1);
    setSelectedTab(newValue);
  };

  const renderJobs = () => {
    let jobs: JobSummaryOutput[] = [];

    if (jobsFilterStore.isSearching) {
      jobs = jobsFilterStore.matchedReports;
    } else {
      switch (selectedTab) {
        case TabType.ActiveReports:
          jobs = jobsFilterStore?.currentReports ?? [];
          break;
        case TabType.CompletedReports:
          jobs = jobsFilterStore?.completedReports ?? [];
          break;
        case TabType.AllReports:
          jobs = jobsFilterStore?.allReports ?? [];
          break;
        default:
          break;
      }
    }

    return (
      <>
        {jobs.length === 0 ? (
          <Grid
            item
            xs={12}
            lg
            sx={{
              background: `${theme.palette.background.paper}`,
              padding: noDataPadding,
            }}
          >
            <Typography variant="body2" style={{ padding: '16px' }}>
              {jobsFilterStore.matchedReports.length === 0 ? (
                <>
                  Your search for <b>{inputText}</b> did not match any reports.
                </>
              ) : (
                <>No reports found.</>
              )}
            </Typography>
          </Grid>
        ) : (
          <>
            {!smScreen && !mdScreen && (
              <Grid
                container
                alignItems="stretch"
                sx={{
                  background: `${theme.palette.background.paper}`,
                  padding: `15px ${smScreen ? '24px' : '48px'} 5px`,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  position: 'sticky',
                  top: 0,
                  zIndex: 10, // To hide `Show Details` button from showing through
                }}
              >
                <GridHeader sx={{ paddingLeft: 0 }} {...GRID_SETTINGS.PROPERTY_TYPE}>
                  Property Type
                </GridHeader>
                <GridHeader sx={{ paddingLeft }} {...GRID_SETTINGS.ADDRESS}>
                  Address
                </GridHeader>

                {lgAndUpScreen && (
                  <GridHeader sx={{ paddingLeft }} {...GRID_SETTINGS.LEAD_APPRAISER}>
                    Lead Appraiser
                  </GridHeader>
                )}

                <GridHeader sx={{ paddingLeft }} {...GRID_SETTINGS.JOB_STATUS}>
                  Job Status
                </GridHeader>

                {lgAndUpScreen && (
                  <>
                    <GridHeader sx={{ paddingLeft }} {...GRID_SETTINGS.INSPECTION_STATUS}>
                      Inspection Status
                    </GridHeader>

                    <GridHeader sx={{ paddingLeft }} {...GRID_SETTINGS.DUE_DATE}>
                      Due Date
                    </GridHeader>
                  </>
                )}
                <GridHeader
                  sx={{ paddingLeft, ...(lgAndUpScreen ? { textAlign: 'right', paddingRight: '65px' } : {}) }}
                  {...GRID_SETTINGS.ACTIONS}
                >
                  Diligence Portal
                </GridHeader>
              </Grid>
            )}

            {jobs.map((job) => (
              <div
                key={job.jobToken}
                style={{
                  background: `${theme.palette.background.paper}`,
                  padding: noDataPadding,
                }}
              >
                <Grid
                  container
                  alignItems="stretch"
                  sx={{
                    background: `${theme.palette.background.paper}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      backgroundColor: '#2222220A',
                      cursor: 'arrow',
                    },
                  }}
                  data-testid="jobs-grid-container"
                >
                  <Grid
                    item
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '22.5px' }}
                    {...GRID_SETTINGS.PROPERTY_TYPE}
                  >
                    <PropertyTypeIcon propertyType={job.assetType}></PropertyTypeIcon>
                  </Grid>

                  <Grid item sx={{ textAlign: 'left', padding: cellsPadding }} {...GRID_SETTINGS.ADDRESS}>
                    <Grid container direction="column" spacing={1}>
                      <Grid item>
                        <Typography variant="body2">
                          <strong>{addressFormatter(job.address)}</strong>
                        </Typography>
                        <Typography variant="caption">{`${job.name ?? ''}`}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {lgAndUpScreen && (
                    <Grid
                      item
                      sx={{
                        ...GridTextStyles,
                        overflowX: 'hidden',
                      }}
                      {...GRID_SETTINGS.LEAD_APPRAISER}
                    >
                      {job.leadAppraiser === 'REASSIGN PLEASE' ? (
                        <div>
                          <Typography variant="body2">
                            <strong>Assigned Shortly</strong>
                          </Typography>
                        </div>
                      ) : (
                        <div>
                          <Typography variant="body2">
                            <strong>{job.leadAppraiser}</strong>
                          </Typography>

                          {job.leadAppraiserEmail && (
                            <Tooltip title={job.leadAppraiserEmail} placement="bottom-start">
                              <Typography
                                variant="body2"
                                sx={{
                                  marginTop: '2px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  color: theme.palette.primary.main,
                                }}
                              >
                                <Link
                                  sx={{ display: 'flex' }}
                                  underline="none"
                                  href={`mailto:${job.leadAppraiserEmail}`}
                                >
                                  <MailOutline color="primary" fontSize="small" sx={{ marginRight: '5px' }} />
                                  {job.leadAppraiserEmail}
                                </Link>
                              </Typography>
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </Grid>
                  )}

                  <Grid
                    item
                    sx={{
                      ...GridTextStyles,
                    }}
                    {...GRID_SETTINGS.JOB_STATUS}
                  >
                    <Typography variant="caption">
                      {job.reportStatus ? (
                        <Badge status={StatusToColor[job.reportStatus]} variant="caption">
                          {`${StatusMappings[job.reportStatus]} `}
                        </Badge>
                      ) : (
                        'N/A'
                      )}
                    </Typography>
                  </Grid>

                  {lgAndUpScreen && (
                    <>
                      <Grid item sx={{ ...GridTextStyles }} {...GRID_SETTINGS.INSPECTION_STATUS}>
                        <Typography variant="body2">
                          {job.hasOwnProperty('inspectionDate') ? formatInspectionStatus(job.inspectionDate) : 'N/A'}
                        </Typography>
                      </Grid>

                      <Grid item sx={{ ...GridTextStyles }} {...GRID_SETTINGS.DUE_DATE}>
                        <Typography variant="body2">
                          {job.hasOwnProperty('dueDate') ? formatDueDate(job.dueDate) : 'N/A'}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  <Grid
                    item
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      padding: cellsPadding,
                      '& button ~ button': {
                        marginLeft: '16px',
                      },
                    }}
                    {...GRID_SETTINGS.ACTIONS}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        AmplitudeService.trackEvent('Diligence Details', { token: job.jobToken, name: job.name });
                        navigateByName('diligence', { token: job.jobToken });
                      }}
                      fullWidth={smScreen}
                    >
                      Diligence Details
                    </Button>
                  </Grid>
                </Grid>
              </div>
            ))}
            <Box
              display="flex"
              justifyContent="center"
              style={{
                background: 'rgba(250, 250, 250, 1)',
                padding: '16px 0px 48px 0px',
              }}
            >
              <Pagination
                count={jobsFilterStore.pagesCount(selectedTab)}
                page={jobsFilterStore.page}
                disabled={jobsFilterStore.isSearching}
                onChange={(e, pageIndex) => {
                  jobsFilterStore.setPage(pageIndex);
                }}
              />
            </Box>
          </>
        )}
      </>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="jobs-container">
      <Header />
      <PageHeader>
        {jobsViewStore.loadStatus === 'error' && (
          <Alert aria-label="form-error" severity={jobsViewStore.loadStatus} data-testid="alert">
            {jobsViewStore.loadStatus}
          </Alert>
        )}
        <Box>
          <Grid container alignItems="stretch" direction={smScreen ? 'row' : 'row-reverse'}>
            <Grid item xs={12} md={4} lg={4} textAlign={mdScreen ? 'left' : 'right'}>
              <TextField
                size="small"
                placeholder="Search property address"
                variant="outlined"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'grey.500', mr: 1 }} />,
                  endAdornment: inputText && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClear} edge="end">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  style: { backgroundColor: 'white' },
                  inputProps: { 'data-testid': 'search-textfield' },
                }}
                InputLabelProps={{
                  style: { backgroundColor: 'white', paddingLeft: '16px' },
                }}
                sx={{ backgroundColor: 'white', width: smScreen || isExactLg ? '100%' : '380px', marginBottom: '16px' }}
                fullWidth={smScreen}
              />
            </Grid>
            <Grid item xs={12} md={8} lg={8}>
              {smScreen ? (
                <Select
                  size="small"
                  value={selectedTab}
                  onChange={(event) => handleTabChange(event.target.value as TabType)}
                  IconComponent={ExpandMoreIcon}
                  disabled={jobsFilterStore.isSearching}
                  sx={{
                    borderRadius: '4px',
                    background: `${theme.palette.background.paper}`,
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    color: `${theme.palette.primary.main}`,
                    fontWeight: 'bold',
                    '& .MuiSelect-icon': {
                      color: `${theme.palette.primary.main}`,
                    },
                  }}
                  fullWidth
                >
                  <MenuItem value={TabType.ActiveReports} sx={{ display: 'flex', justifyContent: 'center' }}>
                    Active Appraisals ({jobsFilterStore.currentReportsNumber})
                  </MenuItem>
                  <MenuItem value={TabType.CompletedReports} sx={{ display: 'flex', justifyContent: 'center' }}>
                    Completed Appraisals ({jobsFilterStore.completedReportsNumber})
                  </MenuItem>
                  <MenuItem value={TabType.AllReports} sx={{ display: 'flex', justifyContent: 'center' }}>
                    All Appraisals ({jobsFilterStore.allReportsNumber})
                  </MenuItem>
                </Select>
              ) : (
                <Tabs
                  value={selectedTab}
                  onChange={(event, newValue) => handleTabChange(newValue)}
                  data-testid="tabs-container"
                  TabIndicatorProps={{
                    style: { display: jobsFilterStore.isSearching ? 'none' : 'block' },
                  }}
                >
                  <CustomTab
                    label={`Active Appraisals (${jobsFilterStore.currentReportsNumber})`}
                    disabled={jobsFilterStore.isSearching}
                  />
                  <CustomTab
                    label={`Completed Appraisals (${jobsFilterStore.completedReportsNumber})`}
                    disabled={jobsFilterStore.isSearching}
                  />
                  <CustomTab
                    label={`All Appraisals (${jobsFilterStore.allReportsNumber})`}
                    disabled={jobsFilterStore.isSearching}
                  />
                </Tabs>
              )}
            </Grid>
          </Grid>
        </Box>
      </PageHeader>
      <PageBody>{renderJobs()}</PageBody>
      <Footer />
    </div>
  );
});

export { JobsViewPage };
